import React, { useEffect, useState } from "react";
import { showPointsHistoryAPI } from "../../services/userService.js";
import { useLocation, useNavigate } from "react-router-dom"; // Added useNavigate
import "bootstrap/dist/css/bootstrap.min.css";
import {
  TrophyFill,
  BookHalf,
  Controller,
  LightningFill,
  CalendarEvent,
  FilterCircle,
  Award,
  ArrowLeft, // Added ArrowLeft
  PersonBoundingBox, // Added Person Icon
} from "react-bootstrap-icons";

// --- Configuration & Styles (Same as before) ---
const ACTIVITY_CONFIG = {
  SPORTS: {
    color: "#0d6efd",
    bg: "#e7f1ff",
    gradient: "linear-gradient(135deg, #0d6efd 0%, #4da3ff 100%)",
    icon: <LightningFill />,
  },
  ACADEMIC: {
    color: "#198754",
    bg: "#e6f8ef",
    gradient: "linear-gradient(135deg, #198754 0%, #63e6be 100%)",
    icon: <BookHalf />,
  },
  ENTERTAINMENT: {
    color: "#fd7e14",
    bg: "#fff4e6",
    gradient: "linear-gradient(135deg, #fd7e14 0%, #ffc078 100%)",
    icon: <Controller />,
  },
  COMPETITIONS: {
    color: "#6f42c1",
    bg: "#f3ebff",
    gradient: "linear-gradient(135deg, #6f42c1 0%, #d0bfff 100%)",
    icon: <TrophyFill />,
  },
  DEFAULT: {
    color: "#6c757d",
    bg: "#f8f9fa",
    gradient: "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)",
    icon: <Award />,
  },
};

export default function ShowPointsHistory() {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [userName, setUserName] = useState("");

  const navigate = useNavigate(); // Hook for navigation
  const location = useLocation();
  const { userId } = location.state || {};

  useEffect(() => {
    if (!userId) {
      setError("User ID is missing.");
      setLoading(false);
      return;
    }

    async function fetchHistory() {
      try {
        const data = await showPointsHistoryAPI(userId);
        setHistory(data.history || []);
        setFilteredHistory(data.history || []);
        if (data.history?.length > 0) setUserName(data.userName || "Student");
      } catch (err) {
        setError(err.message || "Failed to load data.");
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [userId]);

  const handleFilter = (type) => {
    setFilterType(type);
    if (type === "ALL") setFilteredHistory(history);
    else setFilteredHistory(history.filter((h) => h.activityType === type));
  };

  // Calculations
  const totalPoints = history.reduce((sum, item) => sum + item.points, 0);
  const totalActivities = history.length;
  const pointsByType = history.reduce((acc, item) => {
    acc[item.activityType] = (acc[item.activityType] || 0) + item.points;
    return acc;
  }, {});

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div
      className="min-vh-100 py-5"
      style={{ backgroundColor: "#f8f9fc", marginTop: "60px" }}
    >
      <div className="container">
        {/* --- Admin Header Section --- */}
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-5 fade-in-up">
          <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-white shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0"
              style={{
                width: "45px",
                height: "45px",
                border: "1px solid #eee",
              }}
              title="Go Back"
            >
              <ArrowLeft size={20} className="text-secondary" />
            </button>
            <div>
              <h2 className="fw-bolder text-dark mb-0 d-flex align-items-center gap-2">
                <PersonBoundingBox className="text-primary" /> {userName}
              </h2>
              <p className="text-muted mb-0 small">
                Viewing student activity history
              </p>
            </div>
          </div>

          <span className=" bg-white text-dark shadow-sm rounded-pill px-4 py-2 border fw-normal">
            Admin View Mode
          </span>
        </div>

        {/* --- Stats Cards (Grid) --- */}
        <div
          className="row g-3 mb-5 fade-in-up"
          style={{ animationDelay: "0.1s" }}
        >
          <StatCard
            title="Total Score"
            value={totalPoints}
            icon={<TrophyFill />}
            gradient="linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)"
            textColor="white"
          />
          <StatCard
            title="Participations"
            value={totalActivities}
            icon={<CalendarEvent />}
            bg="white"
            textColor="dark"
          />
          <StatCard
            title="Top Interest"
            value={getHighestCategory(pointsByType)}
            icon={<Award />}
            bg="white"
            textColor="dark"
            isText
          />
        </div>

        {/* --- Filter Tabs --- */}
        <div
          className="d-flex justify-content-center flex-wrap gap-2 mb-4 fade-in-up"
          style={{ animationDelay: "0.2s" }}
        >
          <FilterButton
            active={filterType === "ALL"}
            onClick={() => handleFilter("ALL")}
            label="All"
            icon={<FilterCircle />}
          />
          {Object.keys(ACTIVITY_CONFIG)
            .filter((k) => k !== "DEFAULT")
            .map((type) => (
              <FilterButton
                key={type}
                active={filterType === type}
                onClick={() => handleFilter(type)}
                label={type}
                color={ACTIVITY_CONFIG[type].color}
              />
            ))}
        </div>

        {/* --- Activity List (Modern Cards) --- */}
        <div className="row g-4 fade-in-up" style={{ animationDelay: "0.3s" }}>
          {filteredHistory.length > 0 ? (
            filteredHistory.map((item) => {
              const config =
                ACTIVITY_CONFIG[item.activityType] || ACTIVITY_CONFIG.DEFAULT;
              return (
                <div key={item.id} className="col-12 col-md-6 col-lg-4">
                  <div className="activity-card h-100 position-relative bg-white rounded-4 shadow-sm p-4 d-flex flex-column justify-content-between">
                    {/* Decorative Gradient Line */}
                    <div
                      className="position-absolute top-0 start-0 w-100 rounded-top-4"
                      style={{ height: "6px", background: config.gradient }}
                    ></div>

                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="icon-box rounded-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: "45px",
                            height: "45px",
                            backgroundColor: config.bg,
                            color: config.color,
                            fontSize: "1.2rem",
                          }}
                        >
                          {config.icon}
                        </div>
                        <div>
                          <span
                            className=" rounded-pill fw-semibold mb-1"
                            style={{
                              backgroundColor: config.bg,
                              color: config.color,
                              fontSize: "0.7rem",
                            }}
                          >
                            {item.activityType}
                          </span>
                          <h6
                            className="fw-bold text-dark mb-0 text-truncate-2"
                            style={{ lineHeight: "1.4", fontSize: "1rem" }}
                          >
                            {item.activityName}
                          </h6>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-end justify-content-between mt-2 pt-3 border-top border-light">
                      <div className="text-muted small d-flex align-items-center">
                        <CalendarEvent className="me-2" />
                        {new Date(item.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                      <div className="text-end">
                        <span
                          className="d-block text-muted small"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Points
                        </span>
                        <span
                          className="fw-bolder fs-4"
                          style={{ color: config.color }}
                        >
                          +{item.points}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12 text-center py-5 text-muted">
              <div className="mb-3 opacity-50">
                <Award size={40} />
              </div>
              <p>No activity history found for this student.</p>
            </div>
          )}
        </div>
      </div>

      {/* --- CSS (Keep exactly as is) --- */}
      <style>{`
        .fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
          transform: translateY(20px);
        }
        @keyframes fadeInUp {
          to { opacity: 1; transform: translateY(0); }
        }
        .activity-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(0,0,0,0.04);
        }
        .activity-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.08) !important;
        }
        .text-truncate-2 {
           display: -webkit-box;
           -webkit-line-clamp: 2;
           -webkit-box-orient: vertical;
           overflow: hidden;
        }
        .filter-btn {
           transition: all 0.2s ease;
           border: 1px solid transparent;
        }
        .filter-btn:hover {
           background-color: #f1f3f5;
           transform: translateY(-1px);
        }
        .filter-btn.active {
           background-color: #fff;
           border-color: #dee2e6;
           box-shadow: 0 4px 6px rgba(0,0,0,0.05);
           font-weight: 600;
        }
        .btn-white {
           background-color: white;
           transition: all 0.2s;
        }
        .btn-white:hover {
           background-color: #f8f9fa;
           transform: translateX(-3px);
        }
      `}</style>
    </div>
  );
}

// --- Sub-Components (Same as before) ---

function StatCard({ title, value, icon, gradient, bg, textColor, isText }) {
  return (
    <div className="col-12 col-md-4">
      <div
        className="card border-0 shadow-sm rounded-4 overflow-hidden h-100"
        style={{ background: gradient || bg }}
      >
        <div className="card-body p-4 d-flex align-items-center justify-content-between">
          <div>
            <p
              className={`mb-1 small text-uppercase fw-bold opacity-75 text-${textColor === "white" ? "white" : "muted"}`}
            >
              {title}
            </p>
            <h3
              className={`fw-bold mb-0 text-${textColor === "white" ? "white" : "dark"} ${isText ? "fs-5" : "display-6"}`}
            >
              {value}
            </h3>
          </div>
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center p-3`}
            style={{
              backgroundColor:
                textColor === "white" ? "rgba(255,255,255,0.2)" : "#f8f9fa",
              color: textColor === "white" ? "white" : "#6c757d",
            }}
          >
            <span className="fs-4 d-flex">{icon}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterButton({ active, onClick, label, icon, color }) {
  return (
    <button
      onClick={onClick}
      className={`filter-btn btn rounded-pill px-4 py-2 d-flex align-items-center gap-2 ${active ? "active" : ""}`}
      style={{ color: active && color ? color : "#495057" }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function LoadingState() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
      <div className="spinner-border text-primary mb-3" role="status"></div>
      <p className="text-muted fw-semibold">Retrieving student records...</p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <div className="text-center">
        <div className="text-danger mb-3">
          <LightningFill size={40} />
        </div>
        <h5 className="text-danger fw-bold">Action Failed</h5>
        <p className="text-muted">{message}</p>
        <button
          onClick={() => window.history.back()}
          className="btn btn-outline-danger btn-sm mt-3"
        >
          Go Back
        </button>
      </div>
    </div>
  );
}

// Helper to find category
function getHighestCategory(pointsMap) {
  let max = 0;
  let maxKey = "N/A";
  Object.entries(pointsMap).forEach(([key, val]) => {
    if (val > max) {
      max = val;
      maxKey = key;
    }
  });
  return maxKey.charAt(0) + maxKey.slice(1).toLowerCase();
}
